// dependencies
import { useMemo } from "react";
// schema
import type { z } from "zod";

import { AiSettingsSchemaWithDefault } from "@performyard/models";
// types
import type { GetCompanyAISettingsResponse } from "@performyard/service-contracts";

type SchemaShape = z.ZodTypeAny;

type UseAiSettingsDefaultsOptions<T extends SchemaShape> = {
	schema: T;
	data: GetCompanyAISettingsResponse | undefined;
	extractDefaults: (allDefaults: z.infer<typeof AiSettingsSchemaWithDefault>) => z.infer<T>;
	extractData: (data: GetCompanyAISettingsResponse) => unknown;
};

export const useAiSettingsDefaults = <T extends SchemaShape>({
	schema,
	data,
	extractDefaults,
	extractData,
}: UseAiSettingsDefaultsOptions<T>): z.infer<T> => {
	const getSchemaDefaults = useMemo(() => {
		const allDefaults = AiSettingsSchemaWithDefault.parse({});
		return extractDefaults(allDefaults);
	}, [extractDefaults]);

	const defaultValues = useMemo(() => {
		if (!data) {
			return getSchemaDefaults;
		}

		const dataToParse = extractData(data);
		const parseResult = schema.safeParse(dataToParse);

		return parseResult.success ? parseResult.data : getSchemaDefaults;
	}, [data, schema, extractData, getSchemaDefaults]);

	return defaultValues;
};
